// ProductShowcase.js — real screenshots of the actual product (a synthetic
// demo project, not a live client's data), tabbed so visitors can click
// through the screens instead of taking pricing copy's word for it.
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gauge,
  Database,
  AlertTriangle,
  SlidersHorizontal,
  PlayCircle,
  Sparkles,
} from "lucide-react";

import overview from "../screenshots/overview.png";
import entities from "../screenshots/entities.png";
import issues from "../screenshots/issues.png";
import rules from "../screenshots/rules.png";
import runs from "../screenshots/runs.png";
import aiAssistant from "../screenshots/ai-assistant.png";

const TABS = [
  {
    key: "overview",
    icon: Gauge,
    label: "Quality dashboard",
    caption: "Every entity's quality score, dimension breakdown, and worst offenders in one view.",
    image: overview,
  },
  {
    key: "entities",
    icon: Database,
    label: "Entities",
    caption: "Everything staged from source — schema, row counts, and profiling status at a glance.",
    image: entities,
  },
  {
    key: "issues",
    icon: AlertTriangle,
    label: "Issues",
    caption: "Every validation failure and integrity violation, traced back to the rule that caught it.",
    image: issues,
  },
  {
    key: "rules",
    icon: SlidersHorizontal,
    label: "Rules",
    caption: "Validation and cleansing rules, reusable across entities — with AI-suggested rules on request.",
    image: rules,
  },
  {
    key: "runs",
    icon: PlayCircle,
    label: "Runs",
    caption: "Every profile, cleanse, and migration run — rows in, rows out, rejects, and duration.",
    image: runs,
  },
  {
    key: "ai",
    icon: Sparkles,
    label: "AI assistant",
    caption: "A real question, answered from live project data — no export, no reconciling by hand.",
    image: aiAssistant,
  },
];

export default function ProductShowcase() {
  const [active, setActive] = useState(0);
  const tab = TABS[active];

  return (
    <section id="product" className="relative py-20">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-0 h-64 w-[85vw] -translate-x-1/2 rounded-[8rem] bg-[radial-gradient(40rem_16rem_at_50%_0%,rgba(96,165,250,0.16),transparent)]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/60 px-3 py-1 text-xs font-medium text-blue-700 backdrop-blur-md">
            See it in action
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            The real product, not a mockup.
          </h2>
          <p className="mt-3 text-slate-600">
            Screens from a live PrismDQ project — a synthetic commercial-leasing demo dataset, so you can see exactly what you'd get without us showing anyone else's data.
          </p>
        </div>

        {/* Tab bar */}
        <div className="mx-auto mt-10 flex max-w-4xl flex-wrap items-center justify-center gap-2">
          {TABS.map((t, i) => (
            <button
              key={t.key}
              onClick={() => setActive(i)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                i === active
                  ? "bg-gradient-to-r from-blue-600 to-cyan-400 text-white shadow"
                  : "border border-blue-100 bg-white/70 text-slate-600 hover:bg-white"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Screenshot */}
        <div className="mx-auto mt-8 max-w-5xl">
          <div className="overflow-hidden rounded-3xl border border-blue-100 bg-white/80 p-2 shadow-2xl backdrop-blur-md sm:p-3">
            <AnimatePresence mode="wait">
              <motion.img
                key={tab.key}
                src={tab.image}
                alt={`PrismDQ ${tab.label} screen: ${tab.caption}`}
                width="1680"
                height="1050"
                loading="lazy"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="w-full rounded-2xl"
              />
            </AnimatePresence>
          </div>
          <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-slate-500">
            {tab.caption}
          </p>
        </div>
      </div>
    </section>
  );
}
